// Payment Tracking convergence — the loan tab rebuilt on Payment System v3
// (WF1, k=0). Schedule-vs-actual ledger from the loan's amortization terms,
// manual payment entry through the servicing orchestrator, the WF1
// allocation drill-in (zero/skipped lines muted, never hidden), a derived
// exceptions strip, the per-loan servicer-fee config, and the frozen pre-v3
// tape as read-only history. FCI settles Seller/Broker/TPS lines directly —
// the ledger is the book of record, so there is no run-approval gate;
// reversal rights are the control.

import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, EmptyState, Skeleton } from "@/design-system/components/ui";
import { AttentionStrip, type AttentionChip } from "../common/AttentionStrip";
import { ServicingSummaryStrip } from "../paymentTracking/ServicingSummaryStrip";
import { ScheduleLedgerTable } from "../paymentTracking/ScheduleLedgerTable";
import { RecordPaymentWizard } from "../paymentTracking/RecordPaymentWizard";
import { PayoffModal } from "../paymentTracking/PayoffModal";
import { AllocationDrawer } from "../paymentTracking/AllocationDrawer";
import { ServicingFeeConfigCard } from "../paymentTracking/ServicingFeeConfigCard";
import { LegacyHistorySection } from "../paymentTracking/LegacyHistorySection";
import { fmtMoney, PERIOD_STATUS_LABEL } from "../paymentTracking/servicingFormat";
import { ReversalConfirmationModal } from "@/components/payments/ReversalConfirmationModal";
import { useServicingLedger, servicingKeys } from "@/hooks/portfolioAdmin/useServicing";
import { invalidateLoanHealth } from "@/hooks/portfolioAdmin/invalidation";
import { useAuth } from "@/hooks/useAuth";
import { useSearchParams } from "react-router-dom";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

interface PaymentTrackingTabProps {
  loan: LoanSummaryDto;
}

export function PaymentTrackingTab({ loan }: PaymentTrackingTabProps) {
  const auth = useAuth();
  const qc = useQueryClient();
  const canRecord =
    auth.hasPermission("Payments.Create") || auth.hasRoleCode("ADMIN");
  // WS10 — Payments.Reverse is granted to E1/E2 (+ ADMIN); the modal
  // surfaces sunset-gated payments (instructed) as "use Adjustment Entry"
  // via the existing 422 hop.
  const canReverse =
    auth.hasPermission("Payments.Reverse") || auth.hasRoleCode("ADMIN");
  const [reverseTarget, setReverseTarget] = useState<string | null>(null);
  // Payoff (WS2) — the loan must still be live (a retired / charged-off loan or
  // a zero balance has nothing to pay off). The server is the authoritative
  // gate (409); this just hides the action when it can't apply.
  const loanIsLive =
    (loan.accountState ?? "") !== "paid-off" &&
    (loan.accountState ?? "") !== "charged-off" &&
    loan.currentBalance > 0;
  const canPayoff =
    (auth.hasPermission("Payoff.Initiate") || auth.hasRoleCode("ADMIN")) && loanIsLive;
  const [payoffOpen, setPayoffOpen] = useState(false);
  const ledgerQuery = useServicingLedger(loan.id);
  // ?action=record-payment (Overview quick action) auto-opens the entry
  // modal; the param is consumed once so closing the modal sticks.
  const [params, setParams] = useSearchParams();
  const [entryOpen, setEntryOpen] = useState(
    () => canRecord && params.get("action") === "record-payment",
  );
  useEffect(() => {
    if (params.get("action") !== "record-payment") return;
    const updated = new URLSearchParams(params);
    updated.delete("action");
    setParams(updated, { replace: true });
  }, [params, setParams]);

  // Deep-linkable drill-in: ?payment=<id> opens the allocation drawer.
  const openPaymentId = params.get("payment");
  const setOpenPaymentId = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("payment", id);
    else updated.delete("payment");
    setParams(updated, { replace: true });
  };

  const ledger = ledgerQuery.data;

  const exceptionChips = useMemo<AttentionChip[]>(() => {
    if (!ledger || ledger.exceptions.length === 0) return [];
    const items = ledger.exceptions.map((e) => ({
      id: `period-${e.periodIndex}`,
      title: `${PERIOD_STATUS_LABEL[e.status]} — expected ${fmtMoney(e.expectedAmount)}, received ${fmtMoney(e.receivedAmount)}`,
      date: e.dueDate,
    }));
    const tone = ledger.summary.severity === "error" ? "error" : "warning";
    return [
      {
        id: "exceptions",
        label: "Payment exceptions",
        count: ledger.exceptions.length,
        tone,
        items,
      },
    ];
  }, [ledger]);

  if (ledgerQuery.isLoading) {
    return (
      <div className="flex flex-col gap-space-4" data-testid="payment-tracking-loading">
        <Card>
          <Skeleton className="h-8 w-1/2" />
        </Card>
        <Card>
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full mt-space-2" />
          <Skeleton className="h-5 w-2/3 mt-space-2" />
        </Card>
      </div>
    );
  }

  if (ledgerQuery.isError || !ledger) {
    return (
      <Card data-testid="payment-tracking-error">
        <p className="text-small text-status-error m-0 mb-space-2">
          Payment tracking could not be loaded.
        </p>
        <Button variant="secondary" size="sm" onClick={() => void ledgerQuery.refetch()}>
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="payment-tracking-tab">
      <ServicingSummaryStrip summary={ledger.summary} />

      <AttentionStrip chips={exceptionChips} testId="payment-exceptions" />

      <Card>
        <div className="flex items-start justify-between gap-space-3 mb-space-3">
          <h3 className="font-display font-bold text-h3 m-0">
            Payment schedule
          </h3>
          <div className="flex items-center gap-space-2">
            {/* Transitions PR-1 — the payoff action's home is now the
                Transitions tab (quotes, the irrevocable Prepayment Notice,
                and every other exit path). This button keeps working until
                PR-3 binds execution to the transition record. */}
            {canPayoff && (
              <span className="text-small text-gray-mid" data-testid="payoff-moved-hint">
                Quotes &amp; exit paths: Transitions tab
              </span>
            )}
            {canPayoff && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPayoffOpen(true)}
                data-testid="record-payoff-button"
              >
                Record payoff
              </Button>
            )}
            {canRecord && ledger.periods.length > 0 && (
              <Button size="sm" onClick={() => setEntryOpen(true)} data-testid="record-payment-button">
                Record payment
              </Button>
            )}
          </div>
        </div>
        {ledger.periods.length === 0 ? (
          <EmptyState
            title="No payment schedule yet"
            description="The schedule derives from the loan's first payment date and term — set them on the loan profile to begin tracking."
          />
        ) : (
          <ScheduleLedgerTable
            periods={ledger.periods}
            onOpenAllocation={(paymentId) => setOpenPaymentId(paymentId)}
            onReverseClick={
              canReverse ? (paymentId) => setReverseTarget(paymentId) : undefined
            }
          />
        )}
      </Card>

      <LegacyHistorySection entries={ledger.legacyEntries} />

      <ServicingFeeConfigCard loanId={loan.id} />

      {entryOpen && (
        <RecordPaymentWizard
          loan={loan}
          periods={ledger.periods}
          nextPayablePeriod={ledger.nextPayablePeriod ?? null}
          boardingFirstPaymentOnFile={ledger.boardingFirstPaymentOnFile ?? false}
          // Borrower-owed fee money that no period's expectedAmount contains.
          // Without it this entry point prefills the bare schedule remainder
          // while the borrower's statement bills schedule + fees — the two
          // mounts of this wizard would then disagree about the same period.
          openBorrowerFeeObligations={ledger.openBorrowerFeeObligations ?? 0}
          open={entryOpen}
          onClose={() => setEntryOpen(false)}
          // Success panel's "View allocation" — the tab's ?payment= deep link.
          onViewAllocation={(paymentId) => setOpenPaymentId(paymentId)}
        />
      )}

      {payoffOpen && (
        <PayoffModal
          loanId={loan.id}
          open={payoffOpen}
          onClose={() => setPayoffOpen(false)}
        />
      )}

      <AllocationDrawer
        paymentId={openPaymentId}
        onClose={() => setOpenPaymentId(null)}
        // Second entry point to the SAME reversal modal/permission — the
        // drawer reaches payments whose period sits outside the schedule's
        // display horizon (pay-ahead children applied months ahead).
        onReverseClick={
          canReverse ? (paymentId) => setReverseTarget(paymentId) : undefined
        }
        // A pay-ahead child's "advanced from" banner re-points the drawer to
        // the originating (parent) payment's allocation.
        onOpenPayment={(paymentId) => setOpenPaymentId(paymentId)}
      />

      <ReversalConfirmationModal
        open={reverseTarget !== null}
        onClose={() => setReverseTarget(null)}
        paymentId={reverseTarget ?? ""}
        onReversed={(result) => {
          // The reversal cascade can touch any downstream payment on this
          // loan: invalidate the schedule ledger + the loan-health badge
          // surface (single severity source) so the table + tab badges
          // refetch — and the reversed payment's own allocation, so a
          // still-open drawer refreshes to the reversed state.
          void qc.invalidateQueries({
            queryKey: servicingKeys.ledger(loan.id),
          });
          void qc.invalidateQueries({
            queryKey: servicingKeys.allocation(result.reversedPaymentId),
          });
          void invalidateLoanHealth(qc, loan.id);
        }}
      />
    </div>
  );
}
